/**
 * Preflight Sync Service
 * Fetches and caches all data needed for offline verification while the device is online.
 * Creates an offline verification session in IndexedDB marked as "ready".
 */

import api from './api';
import { createSession, getSessionByVerification } from './offlineVerificationSession';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Prepare a verification for offline use.
 * Fetches verification details, project info, site coordinates, and checklist — then
 * stores everything in an IndexedDB session.
 *
 * @param {string|number} verificationId - The simple_verifications ID
 * @param {Object} verifierProfile - { userId, name, tier, license }
 * @returns {Promise<{ready: boolean, session: Object, cachedItems: string[], storageUsedMB: string}>}
 */
export async function prepareForOffline(verificationId, verifierProfile) {
  // Check if a session already exists for this verification
  const existing = await getSessionByVerification(verificationId);
  if (existing && existing.status !== 'synced') {
    return {
      ready: true,
      session: existing,
      cachedItems: ['(resumed existing session)'],
      storageUsedMB: await getStorageUsage()
    };
  }

  const cachedItems = [];

  // Fetch verification details
  const verRes = await api.get(`${API_BASE}/simple-verifications/${verificationId}`);
  const verification = verRes.data?.data || verRes.data;
  cachedItems.push('Verification details');

  // Extract server time from response header
  const serverTime = verRes.headers?.['x-server-time'] || new Date().toISOString();

  // Fetch project details
  let project = {};
  const projectId = verification.projectId || verification.project_id;
  if (projectId) {
    try {
      const projRes = await api.get(`${API_BASE}/projects/${projectId}`);
      project = projRes.data?.data || projRes.data || {};
      cachedItems.push('Project details');
    } catch (err) {
      console.warn('Failed to fetch project for preflight:', err.message);
    }
  }

  // Fetch milestone if linked
  let milestone = null;
  const milestoneId = verification.milestoneId || verification.milestone_id;
  if (milestoneId) {
    try {
      const msRes = await api.get(`${API_BASE}/projects/${projectId}/milestones/${milestoneId}`);
      milestone = msRes.data?.data || msRes.data || null;
      cachedItems.push('Milestone details');
    } catch (err) {
      console.warn('Failed to fetch milestone for preflight:', err.message);
    }
  }

  // Extract site coordinates
  const siteCoordinates = extractSiteCoordinates(verification, project);
  if (siteCoordinates.lat && siteCoordinates.lng) {
    cachedItems.push('Site coordinates');
  }

  // Extract checklist
  const checklist = extractChecklist(verification);
  if (checklist.length > 0) {
    cachedItems.push(`Checklist (${checklist.length} items)`);
  }

  // Fetch building codes for professional tier
  let buildingCodes = [];
  if (verification.tier === 'professional') {
    try {
      const codesRes = await api.get(`${API_BASE}/building-codes`);
      buildingCodes = codesRes.data?.data || codesRes.data || [];
      cachedItems.push(`Building codes (${buildingCodes.length})`);
    } catch (err) {
      console.warn('Failed to fetch building codes:', err.message);
    }
  }

  // Request persistent storage (browser may prompt user)
  if (navigator.storage?.persist) {
    try {
      const persisted = await navigator.storage.persist();
      if (persisted) cachedItems.push('Persistent storage granted');
    } catch {
      // Not critical
    }
  }

  // Create the session
  const session = await createSession(verificationId, {
    verification,
    project,
    milestone,
    siteCoordinates,
    checklist,
    buildingCodes,
    verifierProfile,
    serverTime
  });

  cachedItems.push('Offline session created');

  return {
    ready: true,
    session,
    cachedItems,
    storageUsedMB: await getStorageUsage()
  };
}

/**
 * Extract site coordinates from verification or project data
 */
function extractSiteCoordinates(verification, project) {
  // Try verification-level GPS first
  if (verification.gpsLocation) {
    const gps = typeof verification.gpsLocation === 'string'
      ? JSON.parse(verification.gpsLocation)
      : verification.gpsLocation;
    if (gps.lat || gps.latitude) {
      return {
        lat: gps.lat || gps.latitude,
        lng: gps.lng || gps.longitude,
        radius: gps.radius || 100
      };
    }
  }

  // Try project location
  if (project.latitude && project.longitude) {
    return { lat: project.latitude, lng: project.longitude, radius: 100 };
  }
  if (project.location) {
    const loc = typeof project.location === 'string' ? JSON.parse(project.location) : project.location;
    return {
      lat: loc.lat || loc.latitude || 0,
      lng: loc.lng || loc.longitude || 0,
      radius: loc.radius || 100
    };
  }

  return { lat: 0, lng: 0, radius: 100 };
}

/**
 * Extract checklist items from verification data
 */
function extractChecklist(verification) {
  if (Array.isArray(verification.checklist)) {
    return verification.checklist.map((item, idx) => {
      if (typeof item === 'string') {
        return { id: `cl_${idx}`, description: item, required: true };
      }
      return { id: item.id || `cl_${idx}`, description: item.description || item.text || item.label || '', required: item.required !== false };
    });
  }
  return [];
}

/**
 * Get storage usage in MB
 */
async function getStorageUsage() {
  if (navigator.storage?.estimate) {
    const est = await navigator.storage.estimate();
    return ((est.usage || 0) / (1024 * 1024)).toFixed(2);
  }
  return '0';
}

export default { prepareForOffline };
